import { Checkbox, Heading, Tooltip } from '@mm/components';
import CompanyModel from '@mm/state/model/CompanyModel';
import { StoreState } from '@mm/state/store-interfaces';

import { Formik, FormikProps } from 'formik';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import FormFieldWrapper from '../../../../../../../common/components/FormFieldWrapper/FormFieldWrapper';
import { PhoneInput } from '../../../../../../../common/components/PhoneInput/PhoneInput';
import { WrappedTextInput } from '../../../../../../../common/components/WrappedTextInput/WrappedTextInput';
import { getDeviceInfo } from '../../../../../../../common/hooks/use-device-info';
import { useResolutionInfo } from '@mm/hooks';
import CompleteRegisterButton from '../../../2-UserInfo/CompleteRegisterButton/CompleteRegisterButton';
import {
  fields,
  getMainFiledValidationSchema,
  UserInfoFormValues,
} from '../../../2-UserInfo/user-info-form-helpers';
import './WelcomePage.scss';
import { isFacebookUserSelector } from '@mm/state/selectors/userSelector';
import { OnboardingLogosPanel } from '../../../../../XRayOnboarding/OnboardingLogosPanel/OnboardingLogosPanel';
import classNames from 'classnames';

type Props = {
  onStartPress: (
    companyName: string,
    businessEmail: string,
    companyWebsite: string,
    phoneNumber: string,
    smsConsent?: boolean,
    isAgency?: boolean,
    isUsingHighLevel?: boolean,
  ) => void;
  company: CompanyModel;
};

export const WelcomePage = (props: Props) => {
  const { onStartPress, company } = props;
  const { isMobile } = useResolutionInfo();
  const { isAndroid, isIOS } = getDeviceInfo();
  const userCountry = useSelector((store: StoreState) => store.env.userCountry);
  const isFBUser = useSelector(isFacebookUserSelector);

  const [agency, setAgency] = useState(false);
  const [useHighLevel, setUseHighLevel] = useState(false);
  const [isSmsMarketingChecked, setIsSmsMarketingChecked] = useState(false);

  const validationSchema = getMainFiledValidationSchema();
  const initialValues = {
    businessEmail: company.businessEmail ?? '',
    companyName: '',
    companyWebsite: company.website ?? '',
    phoneNumber: company.phoneNumber ?? '',
  };

  const onSignupClick = async (formValues: UserInfoFormValues) => {
    const { businessEmail, companyName, companyWebsite, phoneNumber } =
      formValues;

    onStartPress(
      companyName,
      businessEmail,
      companyWebsite,
      phoneNumber,
      isSmsMarketingChecked,
      agency,
      useHighLevel,
    );
  };

  const getIsValid = (formProps: FormikProps<UserInfoFormValues>) => {
    if (isMobile) {
      return false;
    }
    return !formProps.isValid;
  };

  return (
    <div className="welcome_page_wrapper">
      {!isMobile && (
        <OnboardingLogosPanel className="welcome-page__left-section" />
      )}

      <div className="welcome-page__content">
        <div className="welcome-page__headers">
          <Heading
            className="welcome-page__content-title"
            size={1}
            text="Welcome to Customers.ai"
          />
          <Heading
            className="welcome-page__content-subtitle"
            size={4}
            text="Please tell us more about your business"
          />
        </div>
        <div className="welcome-page__form-container">
          <Formik
            onSubmit={onSignupClick}
            initialValues={initialValues}
            validationSchema={validationSchema}
            // Until the below issue is fixed on mobile only validate on submit
            // due to autocomplete issues with certain combos of Android/Chrome
            // https://github.com/facebook/react/issues/1159
            validateOnChange={!isMobile}
            validateOnBlur={!isMobile}
            enableReinitialize
          >
            {(formProps: FormikProps<UserInfoFormValues>) => (
              <form onSubmit={() => onSignupClick(formProps.values)}>
                <div className="welcome-page__form-wrapper">
                  <WrappedTextInput
                    label="Company Name:"
                    inputClassName="welcome-page__form-input"
                    formProps={formProps}
                    fieldName={fields.companyName}
                  />

                  <Tooltip
                    position="right"
                    color="light"
                    useHover
                    noDelayClose
                    anchorElement={
                      <WrappedTextInput
                        label="Company Website:"
                        inputClassName="welcome-page__form-input"
                        formProps={formProps}
                        fieldName={fields.companyWebsite}
                      />
                    }
                  >
                    Please, list your website’s URL that you would like to
                    collect its visitor information and generate traffic to
                  </Tooltip>

                  <WrappedTextInput
                    label="Business Email Address:"
                    inputClassName="welcome-page__form-input"
                    formProps={formProps}
                    fieldName={fields.businessEmail}
                  />

                  {isFBUser && (
                    <FormFieldWrapper
                      className="welcome-page_form-field-wrapper"
                      label="Phone Number"
                      error={formProps.errors.phoneNumber}
                      touched={formProps.touched.phoneNumber}
                    >
                      <PhoneInput
                        country={userCountry}
                        name={fields.phoneNumber}
                        className="user-info__phone-input"
                        value={formProps.values.phoneNumber}
                        onChange={value =>
                          formProps.setFieldValue('phoneNumber', value)
                        }
                        onBlur={() => formProps.setFieldTouched('phoneNumber')}
                        errorState={
                          formProps.touched.phoneNumber &&
                          !!formProps.errors.phoneNumber
                        }
                      />
                    </FormFieldWrapper>
                  )}

                  <div className="welcome-page__input-group">
                    <Checkbox
                      className="sign-up-form__agency-checkbox"
                      isChecked={agency}
                      name="termsAccepted"
                      onToggle={() => {
                        setAgency(!agency);
                        if (!agency) {
                          setUseHighLevel(false);
                        }
                      }}
                      label="I am an Agency"
                      labelHelpTextClassName="sign-up-form__agency_checkbox-label"
                    />
                    {agency && (
                      <Checkbox
                        className="sign-up-form__agency-checkbox"
                        isChecked={useHighLevel}
                        name="highLevel"
                        onToggle={() => setUseHighLevel(!useHighLevel)}
                        label="I use HighLevel"
                        labelHelpTextClassName="sign-up-form__agency_checkbox-label"
                      />
                    )}
                    <div
                      onClick={() =>
                        setIsSmsMarketingChecked(!isSmsMarketingChecked)
                      }
                      className="sign-up-form__sms-marketing"
                    >
                      <Checkbox
                        name="sms_marketing"
                        isChecked={isSmsMarketingChecked}
                        className="sign-up-form__sms-marketing-checkbox"
                      />
                      <span className="sign-up-form__sms-marketing-label">
                        <span>Agree to receive SMS marketing messages.</span>
                        <span>
                          Please note that you will receive SMS marketing
                          messages through our website, not by text message.
                          Standard message rates may apply. You can unsubscribe
                          anytime by replying STOP.
                        </span>
                      </span>
                    </div>
                  </div>

                  <input
                    className="user-info__submit-input"
                    type="submit"
                    disabled={getIsValid(formProps)}
                  />
                  <CompleteRegisterButton
                    useNewDesign
                    className={classNames(
                      'welcome-page__complete-register-button',
                      {
                        'welcome-page__complete-register-button--disabled':
                          getIsValid(formProps),
                      },
                    )}
                    disabled={getIsValid(formProps)}
                    isMobileApp={isAndroid || isIOS}
                    onSentToMessenger={formProps.handleSubmit}
                  />
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};
